import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { calculateKPProfile, KPAnalysisResult } from '../utils/kpEngine';
import { Compass, Sparkles, Shield, Star, Layers, Activity, Search, Table, BookOpen, Clock } from 'lucide-react';
import { getZodiacName } from '../utils/language';

interface KPViewProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const KPView: React.FC<KPViewProps> = ({ kundali, lang = 'en' }) => {
  const [activeKPTab, setActiveKPTab] = useState<'planets' | 'cusps' | 'significators' | 'rulingPlanets' | 'horary249'>('planets');
  const [horarySearchNumber, setHorarySearchNumber] = useState<string>('');

  const kpData: KPAnalysisResult = calculateKPProfile(kundali);
  const isHi = lang === 'hi';

  const filteredHorary = horarySearchNumber.trim()
    ? kpData.horaryTable249.filter(
        (h) =>
          h.number.toString() === horarySearchNumber.trim() ||
          h.sign.toLowerCase().includes(horarySearchNumber.toLowerCase()) ||
          h.starLord.toLowerCase().includes(horarySearchNumber.toLowerCase()) ||
          h.subLord.toLowerCase().includes(horarySearchNumber.toLowerCase())
      )
    : kpData.horaryTable249.slice(0, 50);

  return (
    <div className="space-y-6">
      {/* KP Header Card */}
      <div className="bg-astro-bg rounded-2xl border border-astro-border p-5 sm:p-6 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-astro-primary text-white flex items-center justify-center font-bold text-lg shadow-xs shrink-0">
              <Compass className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-serif font-bold text-astro-primary">
                {isHi ? 'के.पी. ज्योतिष प्रणाली (कृष्णमूर्ति पद्धति)' : 'KP System (Krishnamurti Padhdhati)'}
              </h2>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? 'नक्षत्र स्वामी, उप-स्वामी (Sub-Lord) एवं 4-चरणीय कार्येश (4-Step Significators)'
                  : 'Star Lord, Sub-Lord & 4-Step Cuspal Significator Precision Engine'}
              </p>
            </div>
          </div>

          {/* Sub-Tab Switcher */}
          <div className="flex flex-wrap gap-1 bg-astro-card p-1 rounded-xl border border-astro-border text-xs font-semibold">
            <button
              onClick={() => setActiveKPTab('planets')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                activeKPTab === 'planets'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <Star className="w-3.5 h-3.5" />
              <span>{isHi ? 'ग्रह उप-स्वामी' : 'Planets & Sub-Lords'}</span>
            </button>
            <button
              onClick={() => setActiveKPTab('cusps')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                activeKPTab === 'cusps'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>{isHi ? 'भाव संधि व कस्प' : 'House Cusps'}</span>
            </button>
            <button
              onClick={() => setActiveKPTab('significators')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                activeKPTab === 'significators'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <Activity className="w-3.5 h-3.5" />
              <span>{isHi ? '4-चरणीय कार्येश' : '4-Step Significators'}</span>
            </button>
            <button
              onClick={() => setActiveKPTab('rulingPlanets')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                activeKPTab === 'rulingPlanets'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <Shield className="w-3.5 h-3.5" />
              <span>{isHi ? 'रूलिंग प्लैनेट्स (RP)' : 'Ruling Planets (RP)'}</span>
            </button>
            <button
              onClick={() => setActiveKPTab('horary249')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                activeKPTab === 'horary249'
                  ? 'bg-astro-primary text-white font-bold shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              <span>{isHi ? '249 प्रश्न तालिका' : 'KP 249 Table'}</span>
            </button>
          </div>
        </div>

        {/* Tab 1: Planetary KP Table */}
        {activeKPTab === 'planets' && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-astro-card text-astro-primary border-b border-astro-border uppercase text-[10px] tracking-wider">
                  <th className="py-2.5 px-3 font-bold">Planet</th>
                  <th className="py-2.5 px-3 font-bold">Degree / Sign</th>
                  <th className="py-2.5 px-3 font-bold">Nakshatra (Pada)</th>
                  <th className="py-2.5 px-3 font-bold text-astro-accent">Sign Lord</th>
                  <th className="py-2.5 px-3 font-bold text-astro-primary">Star Lord (NL)</th>
                  <th className="py-2.5 px-3 font-bold text-astro-accent bg-astro-accent/10">Sub Lord (SL)</th>
                  <th className="py-2.5 px-3 font-bold text-astro-secondary-text">Sub-Sub (SSL)</th>
                  <th className="py-2.5 px-3 font-bold">House</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {kpData.planets.map((p, idx) => (
                  <tr key={idx} className="hover:bg-[#FAF6ED] transition-colors">
                    <td className="py-2.5 px-3 font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{p.planet}</span>
                      {p.isRetrograde && (
                        <span className="text-[9px] bg-astro-secondary/15 text-astro-secondary px-1 py-0.2 rounded font-bold">
                          R
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="font-mono text-astro-text">{p.formattedDegree}</span>{' '}
                      <span className="text-astro-accent font-semibold">({getZodiacName(p.sign, lang)})</span>
                    </td>
                    <td className="py-2.5 px-3 text-astro-secondary-text">
                      {p.nakshatra} <span className="font-mono text-[11px] text-astro-primary">P{p.pada}</span>
                    </td>
                    <td className="py-2.5 px-3 font-semibold text-astro-accent">{p.signLord}</td>
                    <td className="py-2.5 px-3 font-bold text-astro-primary">{p.starLord}</td>
                    <td className="py-2.5 px-3 font-bold text-astro-accent bg-astro-accent/10 rounded-sm">
                      {p.subLord}
                    </td>
                    <td className="py-2.5 px-3 text-astro-secondary-text">{p.subSubLord}</td>
                    <td className="py-2.5 px-3 font-bold text-astro-primary">H{p.house}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2: House Cusps Table */}
        {activeKPTab === 'cusps' && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-astro-card text-astro-primary border-b border-astro-border uppercase text-[10px] tracking-wider">
                  <th className="py-2.5 px-3 font-bold">Cusp / House</th>
                  <th className="py-2.5 px-3 font-bold">Degree / Sign</th>
                  <th className="py-2.5 px-3 font-bold">Nakshatra</th>
                  <th className="py-2.5 px-3 font-bold text-astro-accent">Sign Lord</th>
                  <th className="py-2.5 px-3 font-bold text-astro-primary">Star Lord (NL)</th>
                  <th className="py-2.5 px-3 font-bold text-astro-accent bg-astro-accent/10">Cusp Sub Lord (SL)</th>
                  <th className="py-2.5 px-3 font-bold text-astro-secondary-text">Sub-Sub (SSL)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {kpData.cusps.map((c, idx) => (
                  <tr key={idx} className="hover:bg-[#FAF6ED] transition-colors">
                    <td className="py-2.5 px-3 font-bold text-astro-primary">
                      House {c.houseNumber} {c.houseNumber === 1 && '(Asc / Lagna)'}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="font-mono text-astro-text">{c.formattedDegree}</span>{' '}
                      <span className="text-astro-accent font-semibold">({getZodiacName(c.sign, lang)})</span>
                    </td>
                    <td className="py-2.5 px-3 text-astro-secondary-text">
                      {c.nakshatra} (P{c.pada})
                    </td>
                    <td className="py-2.5 px-3 font-semibold text-astro-accent">{c.signLord}</td>
                    <td className="py-2.5 px-3 font-bold text-astro-primary">{c.starLord}</td>
                    <td className="py-2.5 px-3 font-bold text-astro-accent bg-astro-accent/10 rounded-sm">
                      {c.subLord}
                    </td>
                    <td className="py-2.5 px-3 text-astro-secondary-text">{c.subSubLord}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 3: 4-Step Significators */}
        {activeKPTab === 'significators' && (
          <div className="mt-5 space-y-4">
            <div className="p-3 bg-[#FAF6ED] rounded-xl border border-astro-accent/30 text-xs text-astro-secondary-text">
              <span className="font-bold text-astro-primary">KP 4-Step Significator Hierarchy:</span> Level A (Planets in Star of Occupants - Strongest) → Level B (Occupants) → Level C (Planets in Star of House Lord) → Level D (House Lord).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {kpData.significators.map((sig, idx) => (
                <div key={idx} className="p-4 bg-astro-bg rounded-xl border border-astro-border shadow-2xs space-y-2">
                  <div className="flex items-center justify-between border-b border-astro-border pb-1.5">
                    <span className="font-bold text-sm text-astro-primary">House {sig.houseNumber}</span>
                    <span className="text-[11px] text-astro-accent font-semibold">
                      Lord: {sig.levelD_HouseLord.join(', ')}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-astro-secondary-text text-[11px]">Level A (Star of Occ):</span>
                      <span className="font-bold text-astro-primary">
                        {sig.levelA_PlanetsInStarOfOccupants.length > 0 ? sig.levelA_PlanetsInStarOfOccupants.join(', ') : '—'}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-astro-secondary-text text-[11px]">Level B (Occupants):</span>
                      <span className="font-bold text-astro-accent">
                        {sig.levelB_OccupantPlanets.length > 0 ? sig.levelB_OccupantPlanets.join(', ') : '—'}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-astro-secondary-text text-[11px]">Level C (Star of Lord):</span>
                      <span className="font-medium text-astro-text">
                        {sig.levelC_PlanetsInStarOfHouseLord.length > 0 ? sig.levelC_PlanetsInStarOfHouseLord.join(', ') : '—'}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-1">
                      <span className="text-astro-secondary-text text-[11px]">Level D (House Lord):</span>
                      <span className="font-medium text-astro-secondary-text">
                        {sig.levelD_HouseLord.join(', ')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Ruling Planets (RP) */}
        {activeKPTab === 'rulingPlanets' && (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5 text-xs">
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Day Lord</span>
                <p className="text-base font-bold text-astro-primary">{kpData.rulingPlanets.dayLord}</p>
                <p className="text-[10px] text-astro-secondary-text">Ruler of the planetary weekday at birth/query.</p>
              </div>
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Moon Star Lord</span>
                <p className="text-base font-bold text-astro-primary">{kpData.rulingPlanets.moonStarLord}</p>
                <p className="text-[10px] text-astro-secondary-text">Nakshatra lord of the natal Moon.</p>
              </div>
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Moon Sign Lord</span>
                <p className="text-base font-bold text-astro-primary">{kpData.rulingPlanets.moonSignLord}</p>
                <p className="text-[10px] text-astro-secondary-text">Rashi lord of the natal Moon.</p>
              </div>
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Lagna Star Lord</span>
                <p className="text-base font-bold text-astro-primary">{kpData.rulingPlanets.ascendantStarLord}</p>
                <p className="text-[10px] text-astro-secondary-text">Nakshatra lord of the Ascendant cusp.</p>
              </div>
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Lagna Sign Lord</span>
                <p className="text-base font-bold text-astro-primary">{kpData.rulingPlanets.ascendantSignLord}</p>
                <p className="text-[10px] text-astro-secondary-text">Rashi lord of the Ascendant.</p>
              </div>
              <div className="bg-[#FAF6ED] p-3.5 rounded-xl border border-astro-accent/30 space-y-1">
                <span className="text-[11px] font-bold text-astro-accent uppercase">Node Substitutes</span>
                <div className="text-xs font-semibold text-astro-primary space-y-0.5 mt-1">
                  {kpData.rulingPlanets.nodeSubstitutes.map((ns, idx) => (
                    <div key={idx}>
                      {ns.node} represents: {ns.represents.join(', ')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: KP 249 Table */}
        {activeKPTab === 'horary249' && (
          <div className="mt-5 space-y-4">
            <div className="flex items-center gap-2 max-w-sm">
              <Search className="w-4 h-4 text-astro-accent" />
              <input
                type="text"
                placeholder="Search seed number (1–249) or planet..."
                value={horarySearchNumber}
                onChange={(e) => setHorarySearchNumber(e.target.value)}
                className="w-full text-xs px-3 py-1.5 rounded-lg border border-astro-border bg-astro-bg focus:outline-none focus:border-astro-primary"
              />
            </div>
            <div className="overflow-x-auto max-h-96 scrollbar-thin">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-astro-card text-astro-primary border-b border-astro-border uppercase text-[10px] tracking-wider sticky top-0">
                    <th className="py-2.5 px-3 font-bold">Seed #</th>
                    <th className="py-2.5 px-3 font-bold">Sign (Rashi)</th>
                    <th className="py-2.5 px-3 font-bold">Sign Lord</th>
                    <th className="py-2.5 px-3 font-bold">Star Lord (NL)</th>
                    <th className="py-2.5 px-3 font-bold text-astro-accent">Sub Lord (SL)</th>
                    <th className="py-2.5 px-3 font-bold">Degree Span</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border/60">
                  {filteredHorary.map((row) => (
                    <tr key={row.number} className="hover:bg-[#FAF6ED] transition-colors">
                      <td className="py-2 px-3 font-bold text-astro-primary">#{row.number}</td>
                      <td className="py-2 px-3 font-medium">{row.sign}</td>
                      <td className="py-2 px-3 text-astro-secondary-text">{row.signLord}</td>
                      <td className="py-2 px-3 font-semibold text-astro-primary">{row.starLord}</td>
                      <td className="py-2 px-3 font-bold text-astro-accent bg-astro-accent/10 rounded-sm">
                        {row.subLord}
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px] text-astro-secondary-text">
                        {(row.startDeg % 30).toFixed(2)}° – {(row.endDeg % 30).toFixed(2)}°
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
